/**
 * WordPress dependencies
 */
import { Button } from '@safe-wordpress/components';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import { useState } from '@safe-wordpress/element';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import clsx from 'clsx';
import { store as NAB_DATA, usePluginSetting } from '@nab/data';
import type { ExperimentSuggestion } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';

export const SuggestionsBody = (): JSX.Element => {
	const overviewUrl = useSelect(
		( select ) =>
			select( NAB_DATA ).getAdminUrl(
				'admin.php?page=nelio-ab-testing-overview'
			),
		[]
	);
	const { instantiating, instantiateSuggestionAsExperiment } =
		useExperimentCreator();
	const suggestions = useSelect( ( select ) => {
		const state = select( NAB_DATA ).getSuggestionGenerationState();
		return state.status === 'opportunity-scan-ready'
			? state.suggestions
			: [];
	}, [] );
	const homeUrl = usePluginSetting( 'homeUrl' );
	const [ DEFAULT_SUGGESTION ] = useState< ExperimentSuggestion >( {
		type: 'nab/css',
		name: _x( 'My First Homepage Test', 'text', 'nelio-ab-testing' ),
		description: '',
		url: homeUrl,
		variants: [
			{
				name: '',
				css: '',
				content: [],
			},
		],
		goals: [
			suggestions?.[ 0 ]?.goals[ 0 ] ?? {
				name: '',
				actions: [
					{
						type: 'nab/click',
						selector: '',
					},
				],
			},
		],
	} );

	const createDefaultTestLabel = suggestions.length
		? _x( 'Create Blank Test', 'command', 'nelio-ab-testing' )
		: _x( 'Create First Test', 'command', 'nelio-ab-testing' );

	const creatingDefaultTestLabel = suggestions.length
		? _x( 'Creating Blank Test…', 'command', 'nelio-ab-testing' )
		: _x( 'Creating First Test…', 'command', 'nelio-ab-testing' );

	return (
		<div className="nab-suggestions-body">
			<p className="nab-suggestions-body__intro">
				{ suggestions.length
					? _x(
							'We found a few ideas to get you started',
							'text',
							'nelio-ab-testing'
					  )
					: _x( 'You’re all set!', 'text', 'nelio-ab-testing' ) }
			</p>

			<div className="nab-suggestions-body__content">
				{ !! suggestions.length ? (
					<>
						<div className="nab-suggestions-body__experiment-suggestions">
							{ suggestions.map( ( s, i ) => (
								<ExperimentSuggestionView
									key={ i }
									value={ s }
									isBusy={ instantiating === s }
									isDisabled={ !! instantiating }
									onCreateExperiment={ () =>
										instantiateSuggestionAsExperiment( s )
									}
								/>
							) ) }
						</div>
					</>
				) : (
					<p>
						{ _x(
							'Your website is ready for A/B testing. Create your first test whenever you’re ready.',
							'user',
							'nelio-ab-testing'
						) }
					</p>
				) }
			</div>

			<div
				className={ clsx( {
					'nab-suggestions-body__actions': true,
					'nab-suggestions-body__actions--has-top-bar':
						! suggestions.length,
				} ) }
			>
				<Button
					className="nab-suggestions-body__create-default-test"
					variant={ suggestions.length ? 'link' : 'primary' }
					isBusy={ instantiating === DEFAULT_SUGGESTION }
					disabled={ !! instantiating }
					onClick={ () =>
						instantiateSuggestionAsExperiment( DEFAULT_SUGGESTION )
					}
				>
					{ instantiating === DEFAULT_SUGGESTION
						? creatingDefaultTestLabel
						: createDefaultTestLabel }
				</Button>
				<Button
					className="nab-suggestions-body__skip"
					variant="link"
					disabled={ !! instantiating }
					onClick={ () => {
						window.location.href = overviewUrl;
					} }
				>
					{ _x( 'Skip', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
};

// ============
// HELPER VIEWS
// ============

function ExperimentSuggestionView( {
	value,
	isBusy,
	isDisabled,
	onCreateExperiment,
}: {
	readonly value: ExperimentSuggestion;
	readonly isBusy: boolean;
	readonly isDisabled: boolean;
	readonly onCreateExperiment: () => void;
} ): JSX.Element {
	return (
		<div className="nab-suggestions-body__experiment-suggestion">
			<div className="nab-suggestions-body__experiment-suggestion-title">
				{ value.name }
			</div>
			{ !! value.description && (
				<div className="nab-suggestions-body__experiment-suggestion-description">
					{ value.description }
				</div>
			) }
			<div className="nab-suggestions-body__experiment-suggestion-actions">
				<Button
					variant="primary"
					isBusy={ isBusy }
					disabled={ isDisabled }
					onClick={ onCreateExperiment }
				>
					{ isBusy
						? _x( 'Creating Test…', 'command', 'nelio-ab-testing' )
						: _x( 'Create Test', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
}

// =====
// HOOKS
// =====

function useExperimentCreator(): {
	readonly instantiating?: ExperimentSuggestion;
	readonly instantiateSuggestionAsExperiment: (
		suggestion: ExperimentSuggestion
	) => void;
} {
	const { instantiateSuggestionAsExperiment } = useDispatch( NAB_DATA );
	const suggestionGenerationState = useSelect(
		( select ) => select( NAB_DATA ).getSuggestionGenerationState(),
		[]
	);

	if ( suggestionGenerationState.status !== 'opportunity-scan-ready' ) {
		return {
			instantiateSuggestionAsExperiment: () => void null,
		};
	}

	return {
		instantiating: suggestionGenerationState.instantiatingSuggestion,
		instantiateSuggestionAsExperiment,
	};
}
